/**
 * IPL Team Name Mapping Utility
 * Maps franchise names from Cricbuzz API to city display names for frontend.
 * Backend stores franchise names as-is; frontend uses city names for display.
 */

// Full name -> City name mapping
const TEAM_NAME_MAP = {
  "Royal Challengers Bengaluru": "Bengaluru",
  "Royal Challengers Bangalore": "Bengaluru", // Alternate spelling
  "Mumbai Indians": "Mumbai",
  "Chennai Super Kings": "Chennai",
  "Kolkata Knight Riders": "Kolkata",
  "Rajasthan Royals": "Rajasthan",
  "Delhi Capitals": "Delhi",
  "Punjab Kings": "Punjab",
  "Sunrisers Hyderabad": "Hyderabad",
  "Gujarat Titans": "Gujarat",
  "Lucknow Super Giants": "Lucknow",
};

// Short name -> City short name mapping
const SHORT_NAME_MAP = {
  "RCB": "BLR",
  "MI": "MUM",
  "CSK": "CHE",
  "KKR": "KOL",
  "RR": "RAJ",
  "DC": "DEL",
  "PBKS": "PUN",
  "SRH": "HYD",
  "GT": "GUJ",
  "LSG": "LKN",
};

// Full name -> City short code mapping
const FULL_NAME_TO_SHORT = {
  "Royal Challengers Bengaluru": "BLR",
  "Royal Challengers Bangalore": "BLR",
  "Mumbai Indians": "MUM",
  "Chennai Super Kings": "CHE",
  "Kolkata Knight Riders": "KOL",
  "Rajasthan Royals": "RAJ",
  "Delhi Capitals": "DEL",
  "Punjab Kings": "PUN",
  "Sunrisers Hyderabad": "HYD",
  "Gujarat Titans": "GUJ",
  "Lucknow Super Giants": "LKN",
};

// Reverse mapping: city -> franchise for reference
const CITY_TO_FRANCHISE = {
  "Bengaluru": "Royal Challengers Bengaluru",
  "Mumbai": "Mumbai Indians",
  "Chennai": "Chennai Super Kings",
  "Kolkata": "Kolkata Knight Riders",
  "Rajasthan": "Rajasthan Royals",
  "Delhi": "Delhi Capitals",
  "Punjab": "Punjab Kings",
  "Hyderabad": "Sunrisers Hyderabad",
  "Gujarat": "Gujarat Titans",
  "Lucknow": "Lucknow Super Giants",
};

/**
 * Get city display name from franchise name
 * @param {string} teamName - Franchise name from API (e.g., "Chennai Super Kings")
 * @returns {string} - City name for display (e.g., "Chennai")
 */
export function getDisplayName(teamName) {
  if (!teamName) return teamName;
  return TEAM_NAME_MAP[teamName] || teamName;
}

/**
 * Get city short code from franchise short name OR full name
 * @param {string} name - Franchise short name (e.g., "CSK") or full name (e.g., "Chennai Super Kings")
 * @returns {string} - City short code for display (e.g., "CHE")
 */
export function getDisplayShortName(name) {
  if (!name) return name;
  // First check full name map
  if (FULL_NAME_TO_SHORT[name]) return FULL_NAME_TO_SHORT[name];
  // Then check short name map
  return SHORT_NAME_MAP[name] || name;
}

/**
 * Check if a team name is an IPL franchise
 * @param {string} teamName - Team name to check
 * @returns {boolean}
 */
export function isIPLTeam(teamName) {
  return teamName in TEAM_NAME_MAP || teamName in CITY_TO_FRANCHISE;
}

/**
 * Format match display with city names
 * @param {object} match - Match object with team1, team2, team1_short, team2_short
 * @returns {object} - Match with display names added
 */
export function formatMatchDisplay(match) {
  if (!match) return match;
  return {
    ...match,
    team1_display: getDisplayName(match.team1),
    team2_display: getDisplayName(match.team2),
    team1_short_display: getDisplayShortName(match.team1_short),
    team2_short_display: getDisplayShortName(match.team2_short),
  };
}

// Team colors for IPL teams (by city name)
export const IPL_TEAM_COLORS = {
  "Bengaluru": { bg: "from-red-600 to-red-800", accent: "#D01C1F" },
  "Mumbai": { bg: "from-blue-500 to-blue-700", accent: "#004BA0" },
  "Chennai": { bg: "from-yellow-400 to-yellow-600", accent: "#FFCC00" },
  "Kolkata": { bg: "from-purple-600 to-purple-800", accent: "#3A225D" },
  "Rajasthan": { bg: "from-pink-500 to-pink-700", accent: "#EA1A85" },
  "Delhi": { bg: "from-blue-600 to-red-600", accent: "#0078BC" },
  "Punjab": { bg: "from-red-500 to-red-700", accent: "#ED1C24" },
  "Hyderabad": { bg: "from-orange-500 to-orange-700", accent: "#FF822A" },
  "Gujarat": { bg: "from-cyan-500 to-blue-600", accent: "#1C1C1C" },
  "Lucknow": { bg: "from-cyan-400 to-blue-500", accent: "#00A3E0" },
  // Fallback for franchise names
  "Royal Challengers Bengaluru": { bg: "from-red-600 to-red-800", accent: "#D01C1F" },
  "Mumbai Indians": { bg: "from-blue-500 to-blue-700", accent: "#004BA0" },
  "Chennai Super Kings": { bg: "from-yellow-400 to-yellow-600", accent: "#FFCC00" },
  "Kolkata Knight Riders": { bg: "from-purple-600 to-purple-800", accent: "#3A225D" },
  "Rajasthan Royals": { bg: "from-pink-500 to-pink-700", accent: "#EA1A85" },
  "Delhi Capitals": { bg: "from-blue-600 to-red-600", accent: "#0078BC" },
  "Punjab Kings": { bg: "from-red-500 to-red-700", accent: "#ED1C24" },
  "Sunrisers Hyderabad": { bg: "from-orange-500 to-orange-700", accent: "#FF822A" },
  "Gujarat Titans": { bg: "from-cyan-500 to-blue-600", accent: "#1C1C1C" },
  "Lucknow Super Giants": { bg: "from-cyan-400 to-blue-500", accent: "#00A3E0" },
};

export function getIPLTeamColor(teamName) {
  const displayName = getDisplayName(teamName);
  return IPL_TEAM_COLORS[displayName] || IPL_TEAM_COLORS[teamName] || { bg: "from-gray-600 to-gray-800", accent: "#4B5563" };
}

export { TEAM_NAME_MAP, SHORT_NAME_MAP, CITY_TO_FRANCHISE };
